// ============================================================
// Shared client-side image optimizer for upload paths.
// Downscales to <= 1600px on the longest side and compresses to
// roughly <= 350 KB before upload, so Supabase Storage objects and
// proposal images stay small. Browser-only (uses canvas/Image/
// FileReader) — import from client components, never from Node.
//
// Lifted verbatim from the original VisualLibraryPage so both the
// new admin upload path and any future caller share one copy.
// ============================================================

export type OptimizedImage = {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
  contentType: string;
};

function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Unable to read image."));
    };
    reader.onerror = () => reject(reader.error || new Error("Unable to read image."));
    reader.readAsDataURL(file);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image."));
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Unable to process image."));
      },
      type,
      quality
    );
  });
}

export async function optimizeImageForStorage(file: File): Promise<OptimizedImage> {
  const sourceUrl = await fileToDataUrl(file);
  const image = await loadImage(sourceUrl);
  const maxDimension = 1600;
  const longestSide = Math.max(image.width, image.height) || 1;
  const scale = Math.min(1, maxDimension / longestSide);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Unable to process image.");

  context.drawImage(image, 0, 0, width, height);

  const hasAlpha = file.type === "image/png" || file.type === "image/webp";
  const type = hasAlpha ? "image/webp" : "image/jpeg";
  const qualitySteps = [0.82, 0.72, 0.62];
  const targetBytes = 350 * 1024;

  let bestBlob: Blob | null = null;
  for (const quality of qualitySteps) {
    const blob = await canvasToBlob(canvas, type, quality);
    bestBlob = blob;
    if (blob.size <= targetBytes) break;
  }

  if (!bestBlob) throw new Error("Unable to process image.");

  return {
    dataUrl: await fileToDataUrl(bestBlob),
    blob: bestBlob,
    width,
    height,
    contentType: type,
  };
}
